import SectionHero from '../Layouts/SectionHero';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Trophy, TrendingUp, Swords, Ghost, Pickaxe, Footprints, Timer, Skull, Search, X, Users, Shield } from 'lucide-react';
import { RATING_CONFIG } from '../../config';
import PodiumSparks from './PodiumSparks';
import ClanRating from './ClanRating';
import RankMovement from './RankMovement';
import styles from './RatingPage.module.css';

// Ячейка игрока: аватар (лицо через прокси gotale.net) + ник.
// При ошибке загрузки аватара показываем плейсхолдер с первой буквой ника.
const PlayerAvatar = ({ username, size = 48 }) => {
  const [failed, setFailed] = useState(false);
  const letter = (username || '?').charAt(0).toUpperCase();
  return failed ? (
    <span className={styles.avatarFallback} style={{ width: size, height: size, fontSize: size * 0.4 }} aria-hidden="true">
      {letter}
    </span>
  ) : (
    <img
      className={styles.avatar}
      src={`${RATING_CONFIG.avatarUrl}/${encodeURIComponent(username)}`}
      alt=""
      loading="lazy"
      width={size}
      height={size}
      onError={() => setFailed(true)}
    />
  );
};

// Категории рейтинга: key — параметр для backend, valueKey — поле в DTO.
// overall — сводная вкладка: показывает все параметры и общий балл.
const CATEGORIES = [
  { key: 'overall', label: 'Общий', icon: Trophy, valueKey: 'overallScore' },
  { key: 'level', label: 'Уровень', icon: TrendingUp, valueKey: 'level' },
  { key: 'players_killed', label: 'Убийства игроков', icon: Swords, valueKey: 'playersKilled' },
  { key: 'mobs_killed', label: 'Убийства мобов', icon: Ghost, valueKey: 'mobsKilled' },
  { key: 'blocks_broken', label: 'Сломано блоков', icon: Pickaxe, valueKey: 'blocksBroken' },
  { key: 'blocks_walked', label: 'Пройдено блоков', icon: Footprints, valueKey: 'blocksWalked' },
  { key: 'playtime', label: 'Время в игре', icon: Timer, valueKey: 'playtimeSeconds' },
  { key: 'deaths', label: 'Смерти', icon: Skull, valueKey: 'deaths' },
];

const BOSS_CATEGORY = { key: 'bosses', label: 'Урон боссам', valueKey: 'totalDamage' };
const PAGE_SIZE = 20;

function formatNumber(n) {
  if (n == null) return '0';
  return Number(n).toLocaleString('ru-RU');
}

function formatPlaytime(seconds) {
  const s = Number(seconds) || 0;
  const hours = Math.floor(s / 3600);
  const minutes = Math.floor((s % 3600) / 60);
  if (hours > 0) return `${formatNumber(hours)} ч ${minutes} мин`;
  return `${minutes} мин`;
}

function formatValue(category, player) {
  const raw = player[category.valueKey];
  if (category.key === 'bosses') return formatNumber(Math.round(Number(raw) || 0));
  if (category.key === 'playtime') return formatPlaytime(raw);
  if (category.key === 'overall') return formatNumber(Math.round(Number(raw) || 0));
  return formatNumber(raw);
}

// Формирует компактный список номеров страниц с многоточиями.
function buildPageItems(current, total) {
  if (total <= 7) {
    return Array.from({ length: total }, (_, i) => i);
  }
  const items = new Set([0, total - 1, current, current - 1, current + 1]);
  const sorted = [...items].filter((p) => p >= 0 && p < total).sort((a, b) => a - b);
  const result = [];
  let prev = null;
  for (const p of sorted) {
    if (prev !== null && p - prev > 1) result.push('...');
    result.push(p);
    prev = p;
  }
  return result;
}

// Медаль для топ-3
function getMedal(rank) {
  if (rank === 1) return { label: 'gold', icon: '🥇' };
  if (rank === 2) return { label: 'silver', icon: '🥈' };
  if (rank === 3) return { label: 'bronze', icon: '🥉' };
  return null;
}

// Статистика для карточки в overall-режиме
const OVERALL_STATS = [
  { key: 'playersKilled', icon: Swords, label: 'Игроки' },
  { key: 'mobsKilled', icon: Ghost, label: 'Мобы' },
  { key: 'blocksBroken', icon: Pickaxe, label: 'Сломано' },
  { key: 'blocksWalked', icon: Footprints, label: 'Пройдено' },
  { key: 'playtimeSeconds', icon: Timer, label: 'Время', format: formatPlaytime },
  { key: 'deaths', icon: Skull, label: 'Смерти' },
];

const RatingPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const isClans = searchParams.get('tab') === 'clans';
  const isBosses = searchParams.get('tab') === 'bosses';
  const currentTab = isClans ? 'clans' : isBosses ? 'bosses' : 'players';
  const [playerCategory, setActiveCategory] = useState(CATEGORIES[0]);
  const activeCategory = isBosses ? BOSS_CATEGORY : playerCategory;
  const [searchInput, setSearchInput] = useState('');
  const searchInputRef = useRef(null);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [players, setPlayers] = useState([]);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [history, setHistory] = useState({});
  const [bosses, setBosses] = useState([]);
  const [selectedBoss, setSelectedBoss] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const requestRef = useRef(null);

  const isOverall = activeCategory.key === 'overall';
  const showPodium = page === 0 && !search && players.length >= 3;

  // Debounce ввода поиска: обновляем фактический фильтр через 400мс.
  useEffect(() => {
    const id = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(0);
    }, 400);
    return () => clearTimeout(id);
  }, [searchInput]);

  // Сброс на первую страницу при смене категории.
  useEffect(() => {
    setPage(0);
  }, [activeCategory]);

  const loadRating = useCallback(() => {
    requestRef.current?.abort();
    if (isClans) return;
    const controller = new AbortController();
    requestRef.current = controller;
    setLoading(true);
    setError(null);
    const params = new URLSearchParams({
      category: activeCategory.key,
      page: String(page),
      size: String(PAGE_SIZE),
    });
    if (search) params.set('search', search);
    if (isBosses && selectedBoss) params.set('boss', selectedBoss);
    const endpoint = isBosses ? `${RATING_CONFIG.apiUrl}/bosses` : RATING_CONFIG.apiUrl;

    fetch(`${endpoint}?${params.toString()}`, { signal: controller.signal })
      .then((res) => {
        if (!res.ok) throw new Error('Не удалось загрузить рейтинг');
        return res.json();
      })
      .then((data) => {
        if (controller.signal.aborted) return;
        setPlayers(Array.isArray(data.players) ? data.players : []);
        if (isBosses) setBosses(Array.isArray(data.bosses) ? data.bosses : []);
        setTotalPages(data.totalPages ?? 0);
        setTotalElements(data.totalElements ?? 0);
        setHistory({ startedAt: data.historyStartedAt, comparedAt: data.weeklyComparedAt });
      })
      .catch((err) => {
        if (!controller.signal.aborted) setError(err.message || 'Ошибка загрузки');
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
  }, [activeCategory, page, search, isClans, isBosses, selectedBoss]);

  useEffect(() => {
    loadRating();
    return () => requestRef.current?.abort();
  }, [loadRating]);

  // Прокрутка к началу при смене страницы.
  const topRef = useRef(null);
  useEffect(() => {
    if (topRef.current) {
      topRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [page]);

  const goToPage = (p) => {
    if (p < 0 || p >= totalPages || p === page) return;
    setPage(p);
  };

  const pageItems = buildPageItems(page, totalPages);

  const renderOverallCard = (player, rank) => {
    const medal = getMedal(rank);
    return (
      <div key={player.username + rank} className={`${styles.card} ${medal ? styles[`card_${medal.label}`] : ''}`}>
        <div className={styles.cardRank}>
          {medal ? (
            <span className={`${styles.rankMedal} ${styles[`medal_${medal.label}`]}`}>
              {medal.icon}
            </span>
          ) : (
            <span className={styles.rankNumber}>#{rank}</span>
          )}
        </div>
        <div className={styles.cardHead}>
          <div className={styles.cardAvatarWrap}>
            <PlayerAvatar username={player.username} size={96} />
          </div>
          <div className={styles.cardPlayerInfo}>
            <Link className={styles.cardName} to={`/players/${encodeURIComponent(player.username)}`}>{player.username}</Link>
            <span className={styles.playerProgress}>
              Уровень {player.level == null ? '—' : formatNumber(player.level)}
              {' · '}
              Престиж {player.prestige == null ? '—' : formatNumber(player.prestige)}
            </span>
            <RankMovement change={player.weeklyChange} comparedAt={history.comparedAt}/>
          </div>
          <div className={styles.cardScore}>
            <span className={styles.scoreValue}>{formatNumber(Math.round(Number(player.overallScore) || 0))}</span>
            <span className={styles.scoreLabel}>Общий балл</span>
          </div>
        </div>
        <div className={styles.cardStats}>
          {OVERALL_STATS.map((stat) => {
            const val = stat.format ? stat.format(player[stat.key]) : formatNumber(player[stat.key]);
            return (
              <div key={stat.key} className={styles.cardStat}>
                <stat.icon className={styles.statIcon} size={18} strokeWidth={1.8} aria-hidden="true" />
                <span className={styles.statValue}>{val}</span>
                <span className={styles.statLabel}>{stat.label}</span>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderSimpleCard = (player, rank) => {
    const medal = getMedal(rank);
    return (
      <div key={player.username + rank} className={`${styles.card} ${styles.cardSimple} ${isBosses ? styles.bossCard : ''} ${medal ? styles[`card_${medal.label}`] : ''}`}>
        <div className={styles.cardRank}>
          {medal ? (
            <span className={`${styles.rankMedal} ${styles[`medal_${medal.label}`]}`}>
              {medal.icon}
            </span>
          ) : (
            <span className={styles.rankNumber}>#{rank}</span>
          )}
        </div>
        <div className={styles.cardMain}>
          <div className={styles.cardPlayer}>
            <PlayerAvatar username={player.username} size={48} />
            <div className={styles.cardPlayerInfo}>
              <Link className={styles.cardName} to={`/players/${encodeURIComponent(player.username)}`}>{player.username}</Link>
              {isBosses && <span className={styles.playerProgress}>
                Уровень {player.level == null ? '—' : formatNumber(player.level)} · Престиж {player.prestige == null ? '—' : formatNumber(player.prestige)}
              </span>}
              <RankMovement change={player.weeklyChange} comparedAt={history.comparedAt}/>
            </div>
          </div>
          <div className={styles.cardScore}>
            <span className={styles.scoreValue}>{formatValue(activeCategory, player)}</span>
            <span className={styles.scoreLabel}>{isBosses ? "Урон боссам" : activeCategory.label}</span>
            {isBosses && <span className={styles.bossKillCount}>Убийств боссов: {formatNumber(player.bossKills)}</span>}
            {activeCategory.key === 'level' && (
              <span className={styles.playerProgress}>
                Престиж {formatNumber(player.prestige)}
              </span>
            )}
          </div>
        </div>
        {isBosses && player.bossVictories?.length > 0 && (
          <details className={styles.bossDetails}>
            <summary aria-label={`Статистика по боссам: ${player.username}`}>Статистика по боссам <span>↗</span></summary>
            <ul>{player.bossVictories.map((boss) => (
              <li key={boss.id} className={selectedBoss === boss.id ? styles.selectedBoss : ''}>
                <span>{boss.name}</span><span className={styles.bossBreakdownValues}><strong>{formatNumber(Math.round(Number(boss.damage) || 0))} урона</strong><span>Убийств: {formatNumber(boss.kills)}</span></span>
              </li>
            ))}</ul>
          </details>
        )}
      </div>
    );
  };

  return (
    <div className={styles.page}>
      <SectionHero
        eyebrow="Имена, которые знает весь сервер"
        title="Рейтинг."
        highlight="Вершина ждёт тебя."
        description={isBosses ? 'Сильнейшие охотники: суммарный урон и убийства боссов за всё время учёта.' : isClans ? 'Сильнейшие кланы, общие достижения и игроки, которые ведут своих к победе.' : 'Каждый уровень и каждая победа имеют значение. Найди себя среди лучших игроков InterWorld.'}
      />

      <div className={styles.body}>
        <nav className={styles.ratingKinds} aria-label="Вид рейтинга">
          {[
            { key: 'players', label: 'Игроки', icon: Users },
            { key: 'clans', label: 'Кланы', icon: Shield },
            { key: 'bosses', label: 'Боссы', icon: Skull },
          ].map((kind) => (
            <button
              key={kind.key}
              type="button"
              className={`${styles.ratingKind} ${kind.key === currentTab ? styles.ratingKindActive : ''}`}
              aria-pressed={kind.key === currentTab}
              onClick={() => {
                if (kind.key === currentTab) return;
                const next = new URLSearchParams(searchParams);
                next.delete('clan');
                if (kind.key === 'players') next.delete('tab');
                else next.set('tab', kind.key);
                setPage(0);
                setSearchParams(next);
              }}
            >
              <kind.icon size={20} aria-hidden="true" />
              {kind.label}
            </button>
          ))}
        </nav>
        {isClans ? <ClanRating /> : <>
        {!isBosses && <div className={styles.tabs}>
          {CATEGORIES.map((cat) => (
            <button
              key={cat.key}
              type="button"
              className={`${styles.tab} ${activeCategory.key === cat.key ? styles.tabActive : ''} ${cat.key === 'overall' ? styles.tabOverall : ''}`}
              onClick={() => { setPage(0); setActiveCategory(cat); }}
              aria-pressed={activeCategory.key === cat.key}
            >
              <cat.icon className={styles.tabIcon} size={18} strokeWidth={1.8} aria-hidden="true" />
              <span>{cat.label}</span>
            </button>
          ))}
        </div>}

        {isBosses && <div className={styles.bossControls}>
          <label htmlFor="rating-boss">Выбрать босса</label>
          <select id="rating-boss" value={selectedBoss} onChange={(e) => { setSelectedBoss(e.target.value); setPage(0); }}>
            <option value="">Все боссы</option>
            {bosses.map((boss) => <option key={boss.id} value={boss.id}>{boss.name}</option>)}
          </select>
          <p>Суммарный урон за всё время учёта в боях, завершившихся убийством босса. Убийство засчитывается каждому участнику, нанёсшему урон.</p>
        </div>}

        <div className={styles.searchBar} role="search" aria-label="Поиск игрока в рейтинге">
          <Search className={styles.searchIcon} size={20} strokeWidth={1.8} aria-hidden="true" />
          <input
            ref={searchInputRef}
            type="search"
            className={styles.searchInput}
            placeholder="Найти игрока по нику"
            aria-label="Никнейм игрока"
            autoComplete="off"
            spellCheck={false}
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
          {searchInput && (
            <button
              type="button"
              className={styles.searchClear}
              onClick={() => {
                setSearchInput('');
                searchInputRef.current?.focus();
              }}
              aria-label="Очистить поиск"
            >
              <X size={18} strokeWidth={1.8} aria-hidden="true" />
            </button>
          )}
        </div>

        {loading ? (
          <div className={styles.loadingState}>
            <div className={styles.spinner} />
            <span>Загрузка рейтинга...</span>
          </div>
        ) : error ? (
          <div className={styles.errorState}>
            <span>⚠️ {error}</span>
            <button type="button" className={styles.retryBtn} onClick={loadRating}>
              Повторить
            </button>
          </div>
        ) : players.length === 0 ? (
          <div className={styles.emptyState}>
            <span className={styles.emptyIcon}>{search ? '🔎' : '📊'}</span>
            <h2>{search ? 'Ничего не найдено' : 'Данных пока нет'}</h2>
            <p>
              {search
                ? `По запросу «${search}» игроки не найдены`
                : isBosses ? 'Статистика появится после участия в убийстве босса' : 'Статистика появится, как только игроки начнут играть'}
            </p>
          </div>
        ) : (
          <>
            {showPodium && (
              <div className={styles.podium}>
                {players.slice(0, 3).map((player, idx) => {
                  const variant = ['gold', 'silver', 'bronze'][idx] || '';
                  return (
                    <div key={player.username + idx} className={styles.podiumCard}>
                      <PodiumSparks variant={variant} />
                      <div className={styles.podiumCardContent}>
                        {isOverall ? renderOverallCard(player, player.rank) : renderSimpleCard(player, player.rank)}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            <div className={styles.cardsGrid} ref={topRef}>
              {isOverall
                ? (showPodium ? players.slice(3) : players).map((player) => renderOverallCard(player, player.rank))
                : (showPodium ? players.slice(3) : players).map((player) => renderSimpleCard(player, player.rank))
              }
            </div>

            {totalPages > 1 && (
              <div className={styles.pagination}>
                <button
                  type="button"
                  className={styles.pageBtn}
                  onClick={() => goToPage(page - 1)}
                  disabled={page === 0}
                  aria-label="Предыдущая страница"
                >
                  ‹
                </button>
                {pageItems.map((item, idx) =>
                  item === '...' ? (
                    <span key={`gap_${idx}`} className={styles.pageGap}>
                      …
                    </span>
                  ) : (
                    <button
                      key={item}
                      type="button"
                      className={`${styles.pageBtn} ${item === page ? styles.pageBtnActive : ''}`}
                      onClick={() => goToPage(item)}
                    >
                      {item + 1}
                    </button>
                  )
                )}
                <button
                  type="button"
                  className={styles.pageBtn}
                  onClick={() => goToPage(page + 1)}
                  disabled={page >= totalPages - 1}
                  aria-label="Следующая страница"
                >
                  ›
                </button>
              </div>
            )}

            <div className={styles.pageInfo}>
              Всего игроков: {formatNumber(totalElements)} · Страница {page + 1} из {totalPages}
            </div>
          </>
        )}
        </>}
      </div>
    </div>
  );
};

export default RatingPage;
